// Crash/error telemetry for the run station (the participant-facing web app).
//
// PRIVACY POSTURE — this is a human-subjects research platform, so this matters:
//   - NO Session Replay. Replay records the participant's screen, which would
//     capture chat messages, survey answers, and task responses (research data
//     and PII). It is deliberately NOT enabled.
//   - sendDefaultPii: false — we never attach IP- or identity-derived data.
//   - The session token (X-Session-Token) is a credential. It is scrubbed from
//     any URL/header that could reach an event or breadcrumb, and must NEVER be
//     passed to setSessionContext.
//   - Events are tagged only with NON-PII run identifiers (session_id,
//     experiment_id, source) so a report tells us WHICH run broke without
//     identifying a person.
//   - No performance tracing (tracesSampleRate: 0): errors are the goal, and
//     tracing would capture more request detail than we want here.
//
// initSentry() is a no-op when VITE_SENTRY_DSN is unset (e.g. local dev), so
// nothing is ever sent unless a DSN is configured for that environment.

import * as Sentry from "@sentry/react";

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// True once init has actually run with a DSN, so context calls stay no-ops otherwise.
let enabled = false;

// Redact a session token if it ever shows up as a query param on a URL.
function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    let touched = false;
    for (const key of ["token", "session_token", "x-session-token"]) {
      if (u.searchParams.has(key)) {
        u.searchParams.set(key, "[redacted]");
        touched = true;
      }
    }
    return touched ? u.toString() : url;
  } catch {
    return url;
  }
}

export function initSentry(): void {
  if (!DSN) return; // no DSN configured -> telemetry off, clean no-op
  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) || undefined,
    sendDefaultPii: false,
    // Errors only. No performance tracing, no profiling, no replay.
    tracesSampleRate: 0,
    // Defensive scrub of the session token from network breadcrumb URLs.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        const data = breadcrumb.data as { url?: string } | undefined;
        if (data && typeof data.url === "string") {
          data.url = scrubUrl(data.url) ?? data.url;
        }
      }
      return breadcrumb;
    },
    // Last-line scrub on the outgoing event: redact the token header / URL.
    beforeSend(event) {
      if (event.request) {
        if (event.request.url) {
          event.request.url = scrubUrl(event.request.url) ?? event.request.url;
        }
        const headers = event.request.headers as Record<string, string> | undefined;
        if (headers) {
          for (const h of Object.keys(headers)) {
            if (h.toLowerCase() === "x-session-token") headers[h] = "[redacted]";
          }
        }
      }
      return event;
    },
  });
  enabled = true;
}

// Tag subsequent events with NON-PII run identifiers so a crash report points at
// the run that broke. NEVER pass the session token or a participant id here.
export function setSessionContext(ctx: {
  sessionId?: string;
  experimentId?: string;
  source?: string;
}): void {
  if (!enabled) return;
  if (ctx.sessionId) Sentry.setTag("session_id", ctx.sessionId);
  if (ctx.experimentId) Sentry.setTag("experiment_id", ctx.experimentId);
  if (ctx.source) Sentry.setTag("source", ctx.source);
}

// Re-export the boundary so the app can wrap its tree to catch render crashes.
export const SentryErrorBoundary = Sentry.ErrorBoundary;
